import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { businessTypesUrl, starterCatalogPreviewUrl, ASSETS } from '../BaseURL';
import { useFacility } from '../context/FacilityContext';
import '../styles/public.css';

// Industry-flavoured copy + accents for the landing page.
// Falls back to a neutral retail tone for any unmapped business type.
const INDUSTRY_THEMES = {
  supermarket: {
    headline: 'Run your <em>supermarket</em> from one screen.',
    lead: 'From cashier to cold-storage, scan groceries, accept Mobile Money and watch your shelves restock themselves.',
    feature: 'Built for grocers',
    sectionTitle: 'Your aisles, organised from day one',
    chips: ['🥬 Fresh produce', '🍞 Bakery & dairy', '💳 MoMo & cash', '📈 Daily P&L'],
  },
  pharmacy: {
    headline: 'A modern POS for your <em>pharmacy</em>.',
    lead: 'Track prescriptions, dispense faster, and keep your antibiotics, pain relief and wellness stock perfectly counted.',
    feature: 'Built for chemists',
    sectionTitle: 'Your dispensary, ready on opening day',
    chips: ['💊 Batch & expiry', '🩺 Prescriptions', '🧴 Wellness ranges', '🔒 Audit trail'],
  },
  hardware: {
    headline: 'The toughest POS for your <em>hardware</em> shop.',
    lead: 'Sell by the metre, the bag or the pallet. Track tools, fasteners, plumbing and cement across every branch.',
    feature: 'Built for builders',
    sectionTitle: 'Your hardware bay, stocked & priced',
    chips: ['🔨 Tools & fasteners', '🧱 Cement by the bag', '⚡ Electrical lines', '🧾 Trade invoices'],
  },
  wholesale: {
    headline: 'A POS that moves <em>cartons</em>, not just cans.',
    lead: 'Sell in bulk units, manage credit customers, and reconcile your warehouse to the cedi every evening.',
    feature: 'Built for distributors',
    sectionTitle: 'Your warehouse, controlled to the carton',
    chips: ['📦 Bulk pricing', '🚛 Delivery notes', '🧾 Credit invoices', '📊 Stock turnover'],
  },
  electronics: {
    headline: 'Sell <em>phones, laptops & appliances</em> with confidence.',
    lead: 'Track IMEIs, manage warranties, and let cashiers offer card, MoMo, layaway or bank-transfer in one tap.',
    feature: 'Built for tech retailers',
    sectionTitle: 'Your showroom, catalogued and priced',
    chips: ['📱 IMEI tracking', '🔋 Warranty support', '💳 Split payments', '🎧 Accessories'],
  },
  boutique: {
    headline: 'A fashion POS that <em>understands sizes</em>.',
    lead: 'Variants by size and colour, lookbook imagery on the till, and customer profiles for repeat clients.',
    feature: 'Built for fashion',
    sectionTitle: 'Your boutique, styled and stocked',
    chips: ['👗 Size/colour variants', '👜 Customer profiles', '🛍️ Lookbook tiles', '🧾 Gift receipts'],
  },
  cosmetics: {
    headline: 'A beauty POS your <em>customers will love</em>.',
    lead: 'Loyalty for repeat shoppers, sample tracking, and a touch-friendly grid of every shade you carry.',
    feature: 'Built for beauty',
    sectionTitle: 'Your beauty bar, ready to glow',
    chips: ['💄 Shade catalog', '🎁 Loyalty points', '🌸 Fragrance lines', '✨ Daily bestsellers'],
  },
  stationery: {
    headline: 'The POS that <em>back-to-school</em> was waiting for.',
    lead: 'Books, pens, paper, art supplies — barcoded, priced and ready before the first parent walks in.',
    feature: 'Built for bookshops',
    sectionTitle: 'Your shelves, stocked for term one',
    chips: ['📚 Bookshelf catalog', '✏️ Bulk class lists', '📓 Term promos', '🧾 School invoices'],
  },
  auto_parts: {
    headline: 'A spare-parts POS that <em>finds the right fit</em>.',
    lead: 'OEM codes, vehicle compatibility, supplier lead-times and brake-pad sets — sorted before the mechanic asks.',
    feature: 'Built for mechanics',
    sectionTitle: 'Your parts wall, indexed and priced',
    chips: ['🔧 OEM lookup', '🛢️ Lubricants & filters', '🚗 Fitment notes', '📦 Supplier reorders'],
  },
  restaurant: {
    headline: 'The POS your <em>kitchen</em> deserves.',
    lead: 'Open tabs, split bills, send orders straight to the kitchen printer and watch portions stay profitable.',
    feature: 'Built for eateries',
    sectionTitle: 'Your menu, ready for the lunch rush',
    chips: ['🍲 Open tabs', '🧾 Split bills', '🖨️ KOT printing', '🥤 Combos & sides'],
  },
  agro: {
    headline: 'A POS that knows the difference between <em>NPK and urea</em>.',
    lead: 'Seeds, feed, fertilisers and cutlasses — tracked by the bag and ready when the farmers come to town.',
    feature: 'Built for farm shops',
    sectionTitle: 'Your agro yard, stocked for the season',
    chips: ['🌾 Seasonal pricing', '🐔 Feed lines', '🌱 Fertilizer tracking', '🧪 Chemicals safety'],
  },
  general_retail: {
    headline: 'A flexible POS for <em>your unique shop</em>.',
    lead: 'Mix groceries, household, snacks and personal care — pricing, stock and reporting that bend to fit your aisle plan.',
    feature: 'Built for retailers',
    sectionTitle: 'Your shop, stocked the way you want it',
    chips: ['🛒 Mixed catalog', '🧼 Personal care', '🍪 Snacks & sweets', '📊 Live reports'],
  },
};

const DEFAULT_THEME = INDUSTRY_THEMES.general_retail;

const Landing = () => {
  const navigate = useNavigate();
  const { configured, facility } = useFacility();
  const [types, setTypes] = useState([]);
  const [showcase, setShowcase] = useState([]);

  const theme = INDUSTRY_THEMES[facility?.business_type] || DEFAULT_THEME;
  const accentLabel = types.find((t) => t.key === facility?.business_type)?.label || 'Retail';

  useEffect(() => {
    axios.get(businessTypesUrl).then((res) => {
      if (res.data?.success) setTypes(res.data.results || []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!facility?.business_type) { setShowcase([]); return; }
    axios.get(starterCatalogPreviewUrl(facility.business_type))
      .then((res) => setShowcase(res.data?.success ? (res.data.items || []) : []))
      .catch(() => setShowcase([]));
  }, [facility?.business_type]);

  const primaryCtaLabel = configured ? 'Sign in' : 'Get started';
  const primaryCtaTarget = configured ? '/login' : '/setup';
  const heroFour = showcase.slice(0, 4);

  return (
    <div className="pub-shell">
      {/* NAV */}
      <nav className="pub-nav">
        <div className="pub-nav-inner">
          <Link to="/" className="pub-brand">
            {facility?.logo ? (
              <img src={`${ASSETS}${facility.logo}`} alt={facility.name} className="brand-logo" />
            ) : (
              <span className="mark">{(facility?.name || 'P').slice(0, 1).toUpperCase()}</span>
            )}
            <span className="name">
              {facility?.name ? (
                <>{facility.name}</>
              ) : (
                <><em>POS</em> Ghana</>
              )}
            </span>
          </Link>
          <div className="pub-nav-links">
            <a href="#showcase">Catalog</a>
            <a href="#features">Features</a>
            <a href="#why">Why us</a>
            <Link to={primaryCtaTarget} className="pub-cta">{primaryCtaLabel}</Link>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <header className="hero">
        <div className="hero-inner">
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="hero-pill">{theme.feature} · {accentLabel}</div>
            <h1 dangerouslySetInnerHTML={{ __html: theme.headline }} />
            <p className="lead">{theme.lead}</p>
            <div className="actions">
              <button className="pub-cta" onClick={() => navigate(primaryCtaTarget)}>
                {primaryCtaLabel} →
              </button>
              <a href="#showcase" className="pub-cta gold">See your catalog</a>
            </div>
            {facility?.name && (
              <p style={{ marginTop: 22, color: 'var(--mute)', fontSize: 13.5 }}>
                Configured for <strong>{facility.name}</strong>
                {facility?.location ? <> — {facility.location}</> : null}.
              </p>
            )}
          </motion.div>

          <motion.div
            className="hero-art industry"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.55, delay: 0.15 }}
            aria-hidden
          >
            <div className="hero-collage">
              {heroFour.map((it, i) => (
                <motion.div
                  key={it.name}
                  className={`hero-cell c${i + 1}`}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.25 + i * 0.08 }}
                >
                  <img src={`${ASSETS}${it.image}`} alt={it.name} />
                </motion.div>
              ))}
              {heroFour.length === 0 && <div className="hero-cell placeholder" />}
            </div>
            <div className="badge-row">
              {theme.chips.map((c) => <span key={c} className="chip">{c}</span>)}
            </div>
          </motion.div>
        </div>
      </header>

      {/* INDUSTRY CATALOG SHOWCASE */}
      <section id="showcase" className="section alt">
        <div className="section-inner">
          <h2>{theme.sectionTitle}</h2>
          <p className="sub">
            Every {accentLabel.toLowerCase()} ships with this starter catalog — fully editable from the dashboard.
          </p>
          <div className="show-grid">
            {showcase.slice(0, 8).map((it, i) => (
              <motion.div
                key={it.name}
                className="show-tile"
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.4, delay: (i % 4) * 0.05 }}
                whileHover={{ y: -4 }}
              >
                <img src={`${ASSETS}${it.image}`} alt={it.name} />
                <div className="meta">
                  <div className="nm">{it.name}</div>
                  <div className="row">
                    <span className="cat">{it.category}</span>
                    <span className="px">GH₵ {Number(it.selling_price).toFixed(2)}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="section">
        <div className="section-inner">
          <h2>Everything a modern retailer needs</h2>
          <p className="sub">From the corner shop to the multi-branch chain, the same platform scales with you.</p>
          <div className="feat-grid">
            {[
              { icon: '🏬', title: 'Multi-Branch Control', text: 'Run unlimited shops from one head office. Monitor sales, stock and staff live from a single dashboard.' },
              { icon: '🛒', title: 'Touch-Optimized POS', text: 'A fast checkout screen for cash, Mobile Money, card, bank transfer and split payments — with thermal-receipt printing.' },
              { icon: '📦', title: 'Smart Inventory', text: 'Stock-in, stock-out, transfers between branches, reorder alerts and offline counts that sync when you reconnect.' },
              { icon: '👥', title: 'Roles & Permissions', text: 'Grant granular permissions to managers, cashiers, store officers and accountants — exact access for each duty.' },
              { icon: '📈', title: 'Live Reports & Profit', text: 'Daily, monthly and branch reports for revenue, profit, expenses, best sellers and customer trends — exportable.' },
              { icon: '🔒', title: 'Audit & Security', text: 'Every login, sale and stock adjustment is logged. Session timeouts and per-action permissions keep you in control.' },
            ].map((f) => (
              <motion.div
                key={f.title}
                className="feat"
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.4 }}
              >
                <div className="icon">{f.icon}</div>
                <h3>{f.title}</h3>
                <p>{f.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="stats-strip">
        <div className="inner">
          <div className="stat-block">
            <div className="num">{showcase.length || '∞'}</div>
            <div className="lbl">Starter products ready</div>
          </div>
          <div className="stat-block">
            <div className="num">5</div>
            <div className="lbl">Payment methods</div>
          </div>
          <div className="stat-block">
            <div className="num">24/7</div>
            <div className="lbl">Offline-first POS</div>
          </div>
          <div className="stat-block">
            <div className="num">GHS</div>
            <div className="lbl">Local currency, MoMo ready</div>
          </div>
        </div>
      </section>

      {/* WHY */}
      <section id="why" className="section">
        <div className="section-inner why-grid">
          <div>
            <h2>Built in Ghana, for Ghanaian business</h2>
            <p className="sub" style={{ marginBottom: 18 }}>
              Mobile Money payments, Ghana Card customer records, regional branch grouping,
              GHS pricing and SMS receipts via local gateways — every detail tuned for how
              business actually happens here.
            </p>
            <ul style={{ color: 'var(--ink-2)', lineHeight: 1.8, paddingLeft: 18 }}>
              <li>Central database, branch isolation by <code>branch_id</code></li>
              <li>Granular permissions per user, not just per role</li>
              <li>Stock transfers between branches with approval workflow</li>
              <li>Receipts via thermal printer, PDF, SMS or WhatsApp</li>
              <li>Audit trail of every login, sale and stock change</li>
            </ul>
          </div>
          <div className="why-collage">
            {showcase.slice(0, 6).map((it, i) => (
              <motion.div
                key={it.name}
                className={`wc-${i + 1}`}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.45, delay: i * 0.06 }}
              >
                <img src={`${ASSETS}${it.image}`} alt={it.name} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-band">
        <h2>Ready to open the doors?</h2>
        <p>Sign in to the dashboard and start ringing up sales. Your catalog, branches and team are already configured for {accentLabel.toLowerCase()}.</p>
        <button className="pub-cta gold" onClick={() => navigate(primaryCtaTarget)}>
          {primaryCtaLabel} →
        </button>
      </section>

      <footer className="pub-foot">
        <div className="inner">
          <div>© {new Date().getFullYear()} {facility?.name || 'POS Ghana'}. All rights reserved.</div>
          <div>Powered by POS Ghana · Made in Accra. 🇬🇭</div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
